import { IQueryParam } from '@/interfaces/common.interface';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  UploadProps,
  message,
} from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import Image from 'components/Image';
import Loading from 'components/Loading';
import COUNTRIES from 'config/countries.json';
import { INIT_PAGINATION } from 'constants/common.constant';
import { EBeamReception } from 'enums/common.enum';
import {
  convertBase64ToFile,
  getBase64,
  getLevel,
  getUniqueKey,
  isBase64Image,
} from 'helpers/common.helper';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams, useSearchParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import {
  getBeamPhotosByUser,
  getBeamPosition,
  getUserDetail,
  removeUserAvatar,
  updateUser,
  uploadUserAvatar,
} from 'store/global/action';
import { useAppDispatch } from 'store/hooks';
import './UserDetail.scss';

const { Option } = Select;

const UserDetail = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();

  const params = useParams();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState<any>(null);
  const [photos, setPhotos] = useState<any[]>([]);
  const [photoParams, setPhotoParams] = useState<IQueryParam>({
    page: INIT_PAGINATION.PAGE,
    limit: INIT_PAGINATION.LIMIT,
  });
  const [photoPagination, setPhotoPagination] = useState<any>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const fetchData = async (id: string) => {
    try {
      const res: any = await unwrapResult(await dispatch(getUserDetail(id)));
      setData(res);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchPhotos = async (id: string) => {
    try {
      const res: any = await unwrapResult(
        await dispatch(
          getBeamPhotosByUser({
            id,
            params: photoParams,
          }),
        ),
      );

      console.log(res);

      if (res && res.items && res.pagination) {
        setPhotos([...photos, ...res.items]);
        setPhotoPagination(res.pagination);
      } else {
        setPhotos([]);
        setPhotoPagination(null);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchMoreData = () => {
    if (photoParams.page && photoParams.page < photoPagination.totalPages) {
      setPhotoParams({ ...params, page: photoParams.page + 1 });
    }
  };

  const onSubmit = async (values: any) => {
    const valid = await form.validateFields();
    if (!valid) return;

    let data: any = {
      fullName: values.fullName,
      phoneNumber: values.phoneNumber,
      countryCode: values.countryCode,
      country: values.country,
      status: values.status,
      beamReception: values.beamReception
        ? EBeamReception.Enable
        : EBeamReception.Disable,
    };

    try {
      setLoading(true);
      await handleUploadAvatar();
      await unwrapResult(
        await dispatch(
          updateUser({
            id: params.id,
            values: data,
          }),
        ),
      );
      message.success('Updated user successfully');
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const uploadAvatarProps: UploadProps = {
    beforeUpload: (file: any) => {
      getBase64(file as RcFile, (data) => {
        setAvatar(data);
      });
      return false;
    },
    showUploadList: false,
  };

  const clearAvatar = () => {
    setAvatar(null);
  };

  const handleUploadAvatar = async () => {
    if (!avatar) {
      return unwrapResult(await dispatch(removeUserAvatar(params.id!)));
    }

    if (avatar && isBase64Image(avatar)) {
      const file = await convertBase64ToFile(avatar);
      return await unwrapResult(
        await dispatch(
          uploadUserAvatar({
            id: params.id,
            file,
          }),
        ),
      );
    }
  };

  const handleAnchor = async (id: string, anchor: string) => {
    try {
      const resBeamPosition: any = await unwrapResult(
        await dispatch(
          getBeamPosition({
            params: {
              userId: id,
              beamId: anchor,
            },
          }),
        ),
      );
      if (resBeamPosition.position > INIT_PAGINATION.LIMIT) {
        setPhotos([]);
        setPhotoPagination(null);
        setPhotoParams({
          page: INIT_PAGINATION.PAGE,
          limit: resBeamPosition.position + 1,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (params?.id) {
      fetchData(params.id);
    }
  }, [params]);

  useEffect(() => {
    if (data?.thumbnail) {
      setAvatar(data.thumbnail.medium);
    }
  }, [data]);

  useEffect(() => {
    if (params?.id) {
      fetchPhotos(params.id);
    }
  }, [params, photoParams]);

  useEffect(() => {
    if (photoPagination) {
      if (photoPagination.currentPage < photoPagination.totalPages) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    }
  }, [photoPagination]);

  useEffect(() => {
    const anchor = searchParams.get('anchor');
    if (anchor && params?.id) {
      handleAnchor(params.id, anchor);
    }
  }, [params, searchParams]);

  useEffect(() => {
    const anchor = searchParams.get('anchor');
    if (photos.length && anchor) {
      console.log(anchor);
      const el = document.getElementById(anchor);
      if (el) {
        el.scrollIntoView();
      }
    }
  }, [photos, searchParams]);

  return (
    <div className="content user-detail-content">
      {data && (
        <>
          <div className="user-detail-content__avatar">
            <Image
              styles={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
              }}
              source={avatar ? avatar : require('assets/img/avatar.png')}
            />

            <div className="user-detail-content__avatar__actions">
              <Upload {...uploadAvatarProps}>
                <Button className="btn btn-primary">Browse</Button>
              </Upload>

              <Button className="btn btn-danger" onClick={clearAvatar}>
                Delete
              </Button>
            </div>
          </div>

          <div className="user-detail-content__form">
            <Form
              name="basic"
              onFinish={onSubmit}
              autoComplete="off"
              layout="vertical"
              className="default"
              form={form}
              initialValues={data}
            >
              <Row>
                <Col md="6">
                  <Form.Item
                    label="Full Name"
                    name="fullName"
                    rules={[
                      { required: true, message: 'Full name is required' },
                      {
                        min: 3,
                        message: 'Full name must be at least 3 characters',
                      },
                      {
                        max: 100,
                        message:
                          'Full name must be no more than 100 characters',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col md="6">
                  <div className="static-form-item">
                    <label>Email</label>
                    <Input value={data.email} disabled />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[
                      {
                        max: 500,
                        message: 'Status must be no more than 500 characters',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col md="6">
                  <div className="static-form-item">
                    <label>Level</label>
                    <Input value={getLevel(data.beamTimes)} disabled />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <div className="static-form-item">
                    <label>Phone Number</label>

                    <Row>
                      <Col md="6">
                        <Form.Item name="countryCode">
                          <Select className="default">
                            {COUNTRIES.data.map((item, index) => (
                              <Option key={getUniqueKey()} value={item.code}>
                                {item.emoji} {item.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col md="6">
                        <Form.Item
                          name="phoneNumber"
                          rules={[
                            {
                              max: 100,
                              message:
                                'Phone number must be no more than 100 characters',
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </Col>

                <Col md="6">
                  <Form.Item label="Country" name="country">
                    <Select className="default">
                      {COUNTRIES.data.map((item, index) => (
                        <Option key={getUniqueKey()} value={item.code}>
                          {item.emoji} {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <Form.Item
                    label="Beam Reception"
                    name="beamReception"
                    valuePropName="checked"
                  >
                    <Switch className="default" />
                  </Form.Item>
                </Col>

                <Col md="6">
                  <div className="static-form-item">
                    <label>Created At</label>
                    <Input
                      value={moment(data.createdAt).format('YYYY-MM-DD')}
                      disabled
                    />
                  </div>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  className="btn btn-primary"
                  htmlType="submit"
                  loading={loading}
                >
                  Update
                </Button>
              </Form.Item>
            </Form>
          </div>
        </>
      )}

      {photos.length > 0 && (
        <div className="px-0 lg:px-[1.875rem] max-w-[50rem]">
          <p className="font-semibold text-center text-[1.5rem]">Feed</p>

          <div>
            <InfiniteScroll
              dataLength={photos.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<Loading />}
              endMessage={
                <p className="all-items-txt">You have seen all items</p>
              }
            >
              {photos.map((item) => (
                <div
                  key={getUniqueKey()}
                  id={item._id}
                  className="flex flex-col items-center justify-center my-[2rem] gap-2"
                >
                  <img
                    alt="photo"
                    src={item.beamPhotoThumbnail?.medium || ''}
                    className="w-full lg:w-1/2 h-auto"
                  />

                  <div className="w-full lg:w-[50%]">
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Sender</span>:{' '}
                      {item.createdBy ? (
                        <>
                          {item.createdBy._id === params.id ? (
                            item.createdBy.fullName
                          ) : (
                            <a
                              target="_blank"
                              href={`/user/${item.createdBy._id}`}
                            >
                              {item.createdBy.fullName}
                            </a>
                          )}
                        </>
                      ) : (
                        '_'
                      )}
                    </p>
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Receiver</span>:{' '}
                      {item.owner ? (
                        <>
                          {item.owner._id === params.id ? (
                            item.owner.fullName
                          ) : (
                            <a target="_blank" href={`/user/${item.owner._id}`}>
                              {item.owner.fullName}
                            </a>
                          )}
                        </>
                      ) : (
                        '_'
                      )}
                    </p>
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Created At</span>:{' '}
                      {item.createdAt
                        ? moment(item.createdAt).format('YYYY-MM-DD HH:mm:ss')
                        : '_'}
                    </p>
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Seen At</span>:{' '}
                      {item.seenAt
                        ? moment(item.seenAt).format('YYYY-MM-DD HH:mm:ss')
                        : '_'}
                    </p>
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Liked</span>:{' '}
                      {item.isLiked ? 'Yes' : '_'}
                    </p>
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Liked At</span>:{' '}
                      {item.likedAt
                        ? moment(item.likedAt).format('YYYY-MM-DD HH:mm:ss')
                        : '_'}
                    </p>
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Status</span>:{' '}
                      {item.status.toUpperCase() || '_'}
                    </p>
                    <p className="mb-0 text-[0.7rem]">
                      <span className="font-semibold">Store</span>:{' '}
                      {item.store.toUpperCase() || '_'}
                    </p>
                  </div>
                </div>
              ))}
            </InfiniteScroll>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
